import React, {
  lazy,
  PropsWithChildren,
  Suspense,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  createGenerateClassName,
  StylesProvider,
  ThemeProvider,
} from "@material-ui/core";
import {
  AVAILABLE,
  ExperimentsHookProvider,
  getExperimentVariant,
} from "@capone/experiments";
import { IntlProvider } from "react-intl";
import { Provider } from "react-redux";
import { Route, Router, Switch } from "react-router-dom";
import {
  CorpSessionInfo,
  TravelWalletClientAssetProps,
  TravelWalletModuleProps,
} from "redmond";
import { AgentBanner, useDeviceTypes } from "halifax";
import { isCorpTenant, UserSourceProvider } from "@capone/common";
import useWindowEventListener from "./hooks/useWindowEventListener";
import { eventsToListen } from "./utils/events";
import * as frenchTranslations from "./lang/fr.json";
import * as englishTranslations from "./lang/en.json";
import ExperimentsProvider, { useExperiments } from "./context/experiments";
import fetchUserInfo from "./api/v1/user/fetchUserInfo";
import AxiosInterceptors from "./components/Counter/AxiosInterceptors";
import { RewardsBanner } from "./modules/rewards/components";
import { store } from "./store";
import { TravelWalletLanding } from "./pages/TravelWalletLanding";
import { PATH_HOME, PATH_TRAVEL_SALE } from "./utils/paths";
import { TravelSaleLanding } from "./pages/TravelSaleLanding";
import { config } from "./api/config";
import { TravelerModalWrapper } from "./pages/TravelerModalWrapper";
import { XMOD_V1_EXPERIMENT } from "../../../cap1-application/b2b-base/src/context/experiments";

function loadLocaleData(locale: string): any {
  switch (locale) {
    case "fr":
      return frenchTranslations;
    default:
      return englishTranslations;
  }
}

const generateClassName = createGenerateClassName({
  productionPrefix: "ptTravelWalletModule",
  seed: "ptTravelWalletModule",
});

export const ClientContext = React.createContext<
  Partial<TravelWalletClientAssetProps>
>({});

const App = (props: TravelWalletModuleProps) => {
  const { experiments, baseHistory, isAgentPortal, clientAssets } = props;
  const { matchesMobile } = useDeviceTypes();
  const [activeTheme, setActiveTheme] = useState(props.theme);
  const [locale, setLocale] = useState(props.language);
  const [messages, setMessages] = useState(loadLocaleData(locale).default);
  const [sessionInfo, setSessionInfo] = useState(clientAssets.sessionInfo);
  const userRoles = isCorpTenant(config.TENANT)
    ? (sessionInfo as CorpSessionInfo).corporateInfo.role
    : [];

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userInfoResponse = await fetchUserInfo();
        setSessionInfo(userInfoResponse);
      } catch (error) {
        console.error(error);
      }
    };
    fetchUser();
  }, []);

  const handleThemeChanged = (e: CustomEvent) => {
    setActiveTheme(e.detail);
    console.log("THEME RECEIVED:", e.detail.palette.type);
  };
  const handleLocaleChanged = (e: CustomEvent) => {
    setLocale(e.detail);
    const messages = loadLocaleData(e.detail);
    setMessages(messages.default);
  };
  useWindowEventListener(eventsToListen.HOST_THEME_CHANGED, handleThemeChanged);
  useWindowEventListener(
    eventsToListen.HOST_LOCALE_CHANGED,
    handleLocaleChanged,
  );

  const { firstName, lastName } = sessionInfo?.userInfo || {
    firstName: "",
    lastName: "",
  };

  return (
    <Provider store={store}>
      <ExperimentsProvider initState={experiments}>
        <ExperimentsHookProvider isLoggedIn={Boolean(sessionInfo?.csrfToken)}>
          <UserSourceProvider>
            <PortalComponentProvider>
              <Router history={baseHistory}>
                <AxiosInterceptors isAgentPortal={isAgentPortal} />
                <ClientContext.Provider
                  value={{ ...clientAssets, sessionInfo }}
                >
                  <div className="App">
                    <StylesProvider generateClassName={generateClassName}>
                      <ThemeProvider theme={activeTheme}>
                        {messages != null ? (
                          <IntlProvider
                            locale={locale}
                            defaultLocale="en"
                            messages={messages}
                          >
                            <Switch>
                              {isAgentPortal ? (
                                <Route path="*">
                                  <AgentBanner
                                    firstName={firstName}
                                    lastName={lastName}
                                  />
                                </Route>
                              ) : props.baseHistory.location.pathname !==
                                PATH_TRAVEL_SALE ? (
                                <Route path="*">
                                  <RewardsBanner />
                                </Route>
                              ) : null}
                            </Switch>
                            <Route path={PATH_TRAVEL_SALE}>
                              <TravelSaleLanding />
                            </Route>
                            <Route path={PATH_HOME} exact>
                              <TravelWalletLanding />
                            </Route>
                          </IntlProvider>
                        ) : (
                          <div>Loading</div>
                        )}
                      </ThemeProvider>
                    </StylesProvider>
                  </div>
                  {isCorpTenant(config.TENANT) && (
                    <TravelerModalWrapper
                      isMobile={matchesMobile}
                      userRoles={userRoles}
                    />
                  )}
                </ClientContext.Provider>
              </Router>
            </PortalComponentProvider>
          </UserSourceProvider>
        </ExperimentsHookProvider>
      </ExperimentsProvider>
    </Provider>
  );
};

const PortalComponentProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const expState = useExperiments();

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const PortalComponent = useMemo(() => {
    if (isCorpTenant(config.TENANT)) {
      return lazy(
        () =>
          import(
            "../../../cap1-application/b2b-base/src/components/Core/CorporatePortal"
          ),
      );
    }
    // only corporate uses b2bportal components in travel wallet
    return null;
  }, []);

  if (PortalComponent) {
    return (
      <Suspense fallback="loading">
        <PortalComponent isXmodRedesign={isXmodRedesign}>
          {children}
        </PortalComponent>
      </Suspense>
    );
  }
  return <>{children}</>;
};

export default App;
